import _ from "lodash";
import React, { useState, useRef, useEffect, cloneElement } from "react";
import ReactDOMServer from "react-dom/server";
import { useI18n, usePrevious } from "Hooks";

// components
import UIBaseContentEditable from "UIComponents/UIBaseContentEditable";
import AudioRecorderSelector from "UIComponents/AudioRecorderSelector";
import MathEquationEditor from "UIComponents/MathEquationEditor";
import UIModal from "UIComponents/UIModal";
import MediaModal from "UIComponents/MediaModal";
import CodeEditor from "UIComponents/CodeEditor";
import MarkdownView from "UIComponents/MarkdownView";
import Graph from "UIComponents/Graph";
import CompactAudioWaveformPlayer from "UIComponents/CompactAudioWaveformPlayer";
import { CreateDropDown } from "Attachment";
import CaptureImage from "Post/routes/CreatePost/components/Screens/CaptureImage";
import MediaCardWrapper from "Attachment/MediaCardWrapper/MediaCardWrapper";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import ConfigureAIVoice from "AppComponents/ConfigureAIVoice";

// modules
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { uploadAttachments } from "Post/modules/PostModule";
import { getUserType } from "modules/CommonSelectors";
import { updateAIData } from "ClassRoom/modules/ClassRoomModule";
import { getSpeechToTextTranscript, hideIntercom } from "modules/Services";

// design system
import { IconButton, Button, Tooltip, DSProvider } from "@toddle-edu/ds-web";
import { fontStyles } from "@toddle-edu/ds-theme";
import {
  SendFilled,
  CloseOutlined,
  ChevronLeftOutlined,
  ChevronRightOutlined,
  CloseCircleFilled,
  TextAlignLeftOutlined,
} from "@toddle-edu/ds-icons";

// constants
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";
import {
  ALLOWED_FILE_TYPES,
  CREATE_DROPDOWN_OPTIONS,
  OPTIONS,
  SCROLL_STEP,
  graphConfig,
} from "../../modules/Constants";
import { AI_TRANSLATION_LANGUAGES } from "@toddle-edu/common-utils";
import { ASSESSMENT } from "Constants/stringConstants";

// utils
import {
  useCallbackRef,
  generateRandomIdV2,
  onEnterOrSpaceKeyDown,
  generateRandomId,
} from "Utils";
import {
  isScrollAtStart,
  isScrollAtEnd,
  replaceMathEquation,
  checkIsSTTLanguageEnabled,
  getAvailableChatOptions,
} from "../../modules/Utils";
import { getIsAITutorEnhancementsV2Enabled } from "Utils/ACLStoreUtils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// third party
import classNames from "classnames";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

// scss
import classes from "./AITutorChatBar.scss";
import { nanoid } from "nanoid";

const uiContentEditable = {
  fontSize: "1.4rem",
  lineHeight: "2rem",
  maxHeight: "150px",
  overflowY: "auto",
  overflowX: "hidden",
  ...fontStyles.medium,
};

const styles = {
  uiContentEditable,
  attachmentContainerStyle: {
    marginBottom: "8px",
    gridAutoFlow: "column",
    gap: "12px",
    overflow: "hidden",
  },
  waveformContainerStyle: {
    backgroundColor: "var(--surface-secondary-enabled)",
  },
  compactUIContentEditable: {
    ...uiContentEditable,
    padding: "0px 0px 16px 0px",
    fontSize: "1.4rem",
  },
  dropdownMenuStyle: {
    zIndex: 1,
  },
};

const COPY_TEX_SCRIPT_ID = "copy-tex";
const loadCopyTexScript = () => {
  const existingScript = document.getElementById(COPY_TEX_SCRIPT_ID);

  if (!existingScript) {
    const script = document.createElement("script");
    script.src = "/copy-tex/copy-tex.min.js";
    script.id = COPY_TEX_SCRIPT_ID;
    script.defer = true;
    document.head.appendChild(script);
  }
};

const LOADER_SIZE = {
  "x-small": "16px",
  medium: "32px",
  small: "28px",
};

const AITutorChatBar = props => {
  const {
    value,
    updateInputField,
    attachmentItems,
    updateAttachmentItems,
    updateAudioTranscripts,
    onBlurInputField,
    onKeyDown,
    placeholder = "common:typeHere",
    onSendMessage,
    aiConversationId,
    setToastMsg,
    isUploading,
    uploadAttachments,
    isSendDisabled,
    isEditMode,
    setEditMessageId = _.noop,
    onClickEditMessage = _.noop,
    userType,
    getSpeechToTextTranscript,
    entityId,
    entityType,
    source,
    updateAIData,
    mathEquationLatex,
    assessmentDetails,
    testId,
    hideIntercom,
    showCompactUI = false,
    gradeId,
    isSpeakingOnly = false,
    userAudioMessageCount = 0,
  } = props;

  // states
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [selectedItem, setSelectedItem] = useState({ action: "", params: {} }); // { action : "ADD_MATH_EQUATION", params: { latex } }
  const [isCreateDropdownVisible, setIsCreateDropdownVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showLeftIcon, setShowLeftIcon] = useState(false);
  const [showRightIcon, setShowRightIcon] = useState(false);
  const [isAudioTranscriptLoading, setIsAudioTranscriptLoading] = useState(
    false
  );
  const [audioAttachments, setAudioAttachments] = useState({});
  const [audioBlob, setAudioBlob] = useState(null);
  const [contentEditableRef, setContentEditableRef] = useCallbackRef();

  // refs
  const [audioRecorderNodeRef, setAudioRecorderNodeRef] = useCallbackRef();
  const [savedSelectionRef, setSavedSelectionRef] = useCallbackRef();
  const attachmentsContainerRef = useRef(null);
  const scrollRef = useRef(null);
  const _scrollRef = scrollRef?.current;

  // variables
  const attachments = attachmentItems?.["ATTACHMENTS"];
  const code = attachmentItems?.["CODE"];
  const graph = attachmentItems?.["GRAPH"];
  const iconSize = showCompactUI ? "medium" : "small";

  // custom hooks
  const { t } = useI18n(["ai", "common"]);
  const prevAttachments = usePrevious(attachments);

  // side effects

  // copy-tex allows copying of latex (rendered through katex plugin in react-markdown)
  useEffect(() => loadCopyTexScript(), []);

  // Update the visibility of icons based on the scroll position
  useEffect(() => {
    if (_scrollRef) {
      setTimeout(() => {
        const isOverflowing = _scrollRef?.scrollWidth > _scrollRef?.clientWidth;

        if (isOverflowing) {
          setShowLeftIcon(!isScrollAtStart(_scrollRef));
          setShowRightIcon(!isScrollAtEnd(_scrollRef));
        } else {
          setShowLeftIcon(false);
          setShowRightIcon(false);
        }
      }, 1000);
    }
  }, [attachments, scrollPosition, _scrollRef]);

  // when a new attachment is added then scroll to the it
  useEffect(() => {
    if (_scrollRef && _.size(attachments) > _.size(prevAttachments)) {
      _scrollRef.scrollTo({
        left: _scrollRef.scrollWidth - _scrollRef.clientWidth,
        behavior: "smooth",
      });
    }
  }, [attachments, prevAttachments]);

  // programmatically close create dropdown on "Upload from device"
  useEffect(() => {
    if (isUploading) setIsCreateDropdownVisible(false);
  }, [isUploading]);

  // some components show intercom on unmount (like CaptureImage)
  // so when the component is closed so hide the intercom
  useEffect(() => {
    if (_.isEmpty(selectedItem?.action)) hideIntercom();
  }, [selectedItem]);

  // functions

  const LoaderSVG = props => {
    const { size = "medium", loaderStyles } = props;

    const loaderWrapperStyles = {
      width: LOADER_SIZE[size],
      height: LOADER_SIZE[size],
      ...loaderStyles,
    };

    return (
      <div
        className={classes.loaderContainer}
        style={loaderWrapperStyles}
        onClick={e => e.stopPropagation()}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 50 50"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="#090909ff"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray="90,150"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 25 25"
              to="360 25 25"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
        </svg>
      </div>
    );
  };

  const isBlob = attachment => _.startsWith(attachment?.url, "blob:");

  // add single or multiple attachments to attachmentItems state
  const addAttachment = async attachments => {
    if (_.isEmpty(attachments)) return;

    const newAttachments = _.castArray(attachments).map(attachment => ({
      ...attachment,
      id: generateRandomId(), // random id with NEW_ prefix (it'll be skipped in mutation variables)
    }));

    updateAttachmentItems(prevItems => ({
      ...prevItems,
      ATTACHMENTS: [...(prevItems?.["ATTACHMENTS"] || []), ...newAttachments],
    }));
  };

  const onFileUpload = ({ attachment }) => {
    if (!getIsAITutorEnhancementsV2Enabled()) {
      setIsAudioTranscriptLoading(true);
    }
    addAttachment(attachment);
  };

  // delete attachment from attachmentItems state
  const deleteAttachment = ({ id }) => {
    const updatedAttachments = _.filter(
      attachments,
      attachment => attachment?.id !== id
    );

    updateAttachmentItems(prevItems => ({
      ...prevItems,
      ATTACHMENTS: updatedAttachments,
    }));

    const attachmentToDelete = _.find(
      attachments,
      attachment => attachment?.id === id
    );

    const isAudioAttachment = attachmentToDelete?.type === "AUDIO";

    if (isAudioAttachment) {
      updateAudioTranscripts([]);
    }
  };

  // set selected action with prev and new state
  const setSelectedAction = params => {
    setSelectedItem(prevState => ({
      ...prevState,
      ...params,
    }));
  };

  // when user tries to add a new math equation, save the current caret position
  // so that we can insert the new equation at the same position
  const saveCaretPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSavedSelectionRef(selection.getRangeAt(0));
    }
  };

  // restore the caret position and insert the new image at the saved caret position
  const restoreCaretPositionAndInsertImage = params => {
    const { height, width, latex, verticalAlign, src } = params;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);

    const img = document.createElement("img");
    img.src = src;
    img.className = "math-equation";
    img.height = height;
    img.width = width;
    img.style.verticalAlign = `${verticalAlign}px`;
    img.style.display = "inline";
    img.alt = latex;
    img.setAttribute("data-latex", latex);
    img.setAttribute("data-id", generateRandomIdV2());

    savedSelectionRef.current.insertNode(img);
    savedSelectionRef.current.collapse(false);

    const updatedValue = contentEditableRef.current.innerHTML;
    updateInputField(updatedValue);
  };

  // show attachments to the right that are out of view
  const handleScrollRight = () => {
    if (_scrollRef) {
      _scrollRef.scrollBy({ left: SCROLL_STEP, behavior: "smooth" });

      const newScrollPosition = scrollPosition + SCROLL_STEP;
      setTimeout(() => setScrollPosition(newScrollPosition), 300);
    }
  };

  // show attachments to the left that are out of view
  const handleScrollLeft = () => {
    if (_scrollRef) {
      _scrollRef.scrollBy({ left: -SCROLL_STEP, behavior: "smooth" });

      const newScrollPosition = scrollPosition - SCROLL_STEP;
      setTimeout(() => setScrollPosition(newScrollPosition), 300);
    }
  };

  // open theatre mode when user clicks on an attachment
  const onClickAttachmentCard = attachment => {
    const index = _.findIndex(attachments, currentAttachment =>
      _.isEqual(currentAttachment?.url, attachment?.url)
    );
    if (index != -1) setSelectedAttachmentIndex(index);

    setShowMediaModal(true);
  };

  // handle math equation paste
  const onPasteMathEquation = pastedHTML => {
    // extract required attributes
    const attributes = {};
    const heightMatch = pastedHTML.match(/height=["']([^"']+)["']/);
    const widthMatch = pastedHTML.match(/width=["']([^"']+)["']/);
    const latexMatch = pastedHTML.match(/data-latex=["']([^"']+)["']/);
    const srcMatch = pastedHTML.match(/src=["']([^"']+)["']/);
    const verticalAlignMatch = pastedHTML.match(/vertical-align:\s?(-?\d+px)/);

    // assign matches to attributes object if they exist
    if (heightMatch) attributes.height = Number(heightMatch[1]);
    if (widthMatch) attributes.width = Number(widthMatch[1]);
    if (latexMatch) attributes.latex = latexMatch[1];
    if (srcMatch) attributes.src = srcMatch[1];
    if (verticalAlignMatch)
      attributes.verticalAlign = parseInt(verticalAlignMatch[1], 10);

    restoreCaretPositionAndInsertImage(attributes);
  };

  // handle paste event in content editable
  const onPaste = e => {
    e.preventDefault();

    const pastedHTML = e.clipboardData.getData("text/html");

    // check if class contains 'math-equation'
    const hasMathEquationClass = /class=["']([^"']*\s)?math-equation(\s[^"']*)?["']/.test(
      pastedHTML
    );

    if (hasMathEquationClass) {
      onPasteMathEquation(pastedHTML);
    } else {
      const pastedText = e.clipboardData.getData("text/plain");
      document.execCommand("insertText", false, pastedText);
    }
  };

  // update the input field value
  const updateInputFieldLocal = value => {
    let formattedValue = _.trim(value);
    if (formattedValue === "<br>") formattedValue = "";
    if (_.endsWith(formattedValue, "</span>")) {
      formattedValue = formattedValue + " ";
    }

    updateInputField(formattedValue);
  };

  // add styles to the math equation image
  const highlightImage = e => {
    const image = e.target;
    image.style.backgroundColor = `rgba(60, 132, 244, 0.25)`;
    image.style.border = `1px solid rgb(60, 132, 244)`;
    image.style.userSelect = "none";
    image.style.caretColor = "transparent";

    // create a range and select the image
    const range = document.createRange();
    range.selectNode(image);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  // remove styles from the math equation image
  const removeHighlight = () => {
    const mathEquations = document.querySelectorAll(".math-equation");

    _.forEach(mathEquations, element => {
      element.style.backgroundColor = "";
      element.style.border = "";
      element.style.userSelect = "";
      element.style.caretColor = "";
    });
  };

  // handles click event on content editable to highlight or remove highlight from math equation
  const onClickContentEditable = e => {
    saveCaretPosition();

    if (e.target && e.target.classList.contains("math-equation")) {
      const isHighlighted =
        e.target.style.backgroundColor === `rgba(60, 132, 244, 0.25)`;

      if (isHighlighted) removeHighlight(e);
      else highlightImage(e);
    } else {
      removeHighlight(e);
    }
  };

  // handles double click event on content editable to edit math equation
  const onDoubleClickContentEditable = e => {
    const latex = _.get(e, "target.dataset.latex");
    const equationId = _.get(e, "target.dataset.id");

    if (latex) {
      highlightImage(e);
      saveCaretPosition();
      setSelectedAction({
        action: OPTIONS.ADD_MATH_EQUATION,
        params: { latex, equationId },
      });
    }
  };

  const handleTranscription = async ({
    attachment,
    shouldReplaceContent = false,
  }) => {
    if (!_.isEmpty(attachment?.id))
      setAudioAttachments(prevState => {
        return {
          ...prevState,
          [attachment?.id]: attachment,
        };
      });

    let tutorLanguageCode;
    if (checkIsSTTLanguageEnabled()) {
      const tutorLanguageId = _.find(
        _.get(assessmentDetails, "allFields", []),
        { uid: "tutorLanguage" }
      )?.value;

      tutorLanguageCode = _.get(
        _.find(AI_TRANSLATION_LANGUAGES, ({ id }) => id == tutorLanguageId),
        "code"
      );
    }

    const transcriptionPayload = {
      entityId,
      entityType,
      language: tutorLanguageCode,
    };

    transcriptionPayload.blobAttachment = attachment;

    const transcript = (await getSpeechToTextTranscript(transcriptionPayload))
      ?.transcript;

    if (transcript) {
      updateAudioTranscripts(prevState => [
        ...prevState,
        {
          id: isBlob ? `BLOB_AUDIO_${nanoid(5)}` : attachment?.id,
          transcript,
        },
      ]);

      if (!isSpeakingOnly) {
        updateInputField(prevValue => {
          if (!_.isEmpty(_.trim(prevValue)) && !shouldReplaceContent) {
            return `${prevValue} ${transcript}`;
          }

          return transcript;
        });
      }
    }

    setIsAudioTranscriptLoading(false);
  };

  const onBlobAttachmentRecieved = async blobAttachment => {
    if (getIsAITutorEnhancementsV2Enabled()) {
      setAudioBlob(blobAttachment);
      return;
    }

    setIsAudioTranscriptLoading(true);
    await handleTranscription({
      attachment: blobAttachment,
    });
  };

  const handleGenerateTranscript = async attachment => {
    setIsAudioTranscriptLoading(true);

    // Use stored audio blob if available, otherwise use the attachment as-is
    const blobAttachment = audioBlob || attachment;
    await handleTranscription({
      attachment: blobAttachment,
      shouldReplaceContent: true,
    });
  };

  // render all the options in the chat bar (record audio, upload file etc.)
  const renderOption = option => {
    const { key, icon, tooltipText } = option;

    const disabled =
      isUploading ||
      isAudioTranscriptLoading ||
      (key === OPTIONS.ADD_AUDIO && _.some(attachments, { type: "AUDIO" })); // allow only one audio

    const buttonComponent = (
      <div className={classes.flex}>
        <ConditionalWrapper
          condition={tooltipText && !disabled && !showCompactUI}
          type="wrapperAsElement"
          wrapper={
            <Tooltip
              dsVersion="2.0"
              tooltip={t(tooltipText)}
              placement={"top"}
              showArrow={true}
            />
          }
        >
          <IconButton
            key={key}
            icon={icon}
            dsVersion={"2.0"}
            variant={"neutral"}
            type={"plain"}
            size={iconSize}
            disabled={disabled}
            testId={`${testId}-aiTutorChatBar-${key}`}
          />
        </ConditionalWrapper>
      </div>
    );

    switch (key) {
      case OPTIONS.ADD_AUDIO: {
        const onAudioAttachmentUploadFinished = async attachment => {
          const existingAttachment = _.find(
            _.get(attachmentItems, "ATTACHMENTS"),
            att => {
              return _.startsWith(att.url, "blob:") && att.type === "AUDIO";
            }
          );
          if (!_.isNil(existingAttachment)) {
            // update existing attachment
            updateAttachmentItems(prevItems => {
              const updatedAttachments = _.map(
                prevItems?.["ATTACHMENTS"],
                att => {
                  if (att.id === existingAttachment?.id)
                    return { ...attachment, id: att.id };
                  else return att;
                }
              );

              return {
                ...prevItems,
                ATTACHMENTS: updatedAttachments,
              };
            });
          }
        };

        return (
          <div key={key} className={classes.flex}>
            <AudioRecorderSelector
              parentType={"AI_CONVERSATION"}
              parentId={aiConversationId}
              setChatbarVisible={value => setIsRecording(!value)}
              header={t("common:add_voice_message")}
              version={"visualiser"}
              onFileUpload={onFileUpload}
              updateInputField={onAudioAttachmentUploadFinished}
              buttonComponent={buttonComponent}
              container={audioRecorderNodeRef}
              disabled={isUploading || isAudioTranscriptLoading}
              showValue={false}
              showProgress={false}
              onBlobAttachmentRecieved={onBlobAttachmentRecieved}
              sourceFrom={"AI_TUTOR"}
            />
          </div>
        );
      }
      case OPTIONS.ADD_ATTACHMENT_FROM_DEVICE: {
        return (
          <CreateDropDown
            exportAsPdf={true}
            parentId={aiConversationId}
            parentType={"AI_CONVERSATION"}
            accept={ALLOWED_FILE_TYPES}
            mode={"edit"}
            multiselect={true}
            buttonComponent={buttonComponent}
            dropDownOptions={CREATE_DROPDOWN_OPTIONS}
            isNewDropdown={true}
            onMultipleFilesUploaded={addAttachment}
            disabled={isUploading || isAudioTranscriptLoading}
            dsVersion={"2.0"}
            enableShareModal={false} // do not show DriveShareModal, directly add as a file
            visible={isCreateDropdownVisible}
            onVisibleChange={setIsCreateDropdownVisible}
            showDisplayText={false} // do not show display text field for link (not required for AI)
            dropdownMenuStyle={styles.dropdownMenuStyle}
          />
        );
      }
      default: {
        return cloneElement(buttonComponent, {
          onClick: () => {
            // load the saved latex (if any) in math equation editor
            if (key === OPTIONS.ADD_MATH_EQUATION && mathEquationLatex) {
              setSelectedAction({
                action: key,
                params: { latex: mathEquationLatex },
              });
            } else {
              setSelectedAction({ action: key });
            }
          },
        });
      }
    }
  };

  // render attachments (audio, image, file etc.) in horizontal scroll view
  const renderAttachments = attachments => {
    if (_.isEmpty(attachments)) return null;

    const onScroll = e => {
      const { scrollWidth, clientWidth } = e.target || {};

      const isOverflowing = scrollWidth > clientWidth;

      if (isOverflowing) {
        setShowLeftIcon(!isScrollAtStart(e.target));
        setShowRightIcon(!isScrollAtEnd(e.target));
      } else {
        setShowLeftIcon(false);
        setShowRightIcon(false);
      }
    };

    const mediaCardContainerClass = classNames({
      [classes.mediaCardContainer]: true,
      [classes.scrollX]: _.size(attachments) > 2,
      [classes.flexStartContent]: _.every(
        attachments,
        attachment => attachment?.type === "IMAGE"
      ),
    });

    return (
      <div
        className={mediaCardContainerClass}
        ref={scrollRef}
        onScroll={onScroll}
      >
        {_.map(attachments, attachment => {
          const { id, type, url } = attachment || {};

          const deleteAttachmentBtn = (
            <IconButton
              onClick={e => {
                e.stopPropagation();
                deleteAttachment({ id });
              }}
              _legacy_size={showCompactUI ? "xx-small" : "xxx-small"}
              size="x-small"
              icon={<CloseOutlined />}
              className={
                showCompactUI ? classes.deleteIconCompact : classes.deleteIcon
              }
              testId={`${testId}-aiTutorChatBar-deleteAttachment`}
            />
          );

          switch (type) {
            case "AUDIO": {
              const audioRecorderClass = classNames(
                classes.cardContainer,
                classes.audioRecorder
              );

              const showGenerateTranscript =
                getIsAITutorEnhancementsV2Enabled() && !isSpeakingOnly;

              const buttonElement = showGenerateTranscript ? (
                <Tooltip
                  tooltip="Generate transcript"
                  placement="top"
                  showArrow
                >
                  <IconButton
                    icon={<TextAlignLeftOutlined />}
                    variant={"neutral"}
                    type={"plain"}
                    size={"small"}
                    disabled={isAudioTranscriptLoading}
                    onClick={() => handleGenerateTranscript(attachment)}
                    testId={`${testId}-toggle-editable`}
                  />
                </Tooltip>
              ) : null;

              return (
                <div
                  className={audioRecorderClass}
                  key={id}
                  ref={attachmentsContainerRef}
                >
                  <CompactAudioWaveformPlayer
                    attachment={attachment}
                    containerStyle={styles.waveformContainerStyle}
                    testId={testId}
                    buttonElement={buttonElement}
                  />
                  {!isAudioTranscriptLoading ? deleteAttachmentBtn : null}
                </div>
              );
            }
            case "IMAGE": {
              const imageClass = classNames(
                classes.cardContainer,
                classes.imageContainer
              );

              const onClickImage = () => {
                onClickAttachmentCard(attachment);
              };

              const onKeyDownImage = e => {
                onEnterOrSpaceKeyDown(e, onClickImage);
              };

              return (
                <div key={id} className={imageClass}>
                  <button
                    tabIndex={0}
                    onClick={onClickImage}
                    onKeyDown={onKeyDownImage}
                    className={classes.imageButtonWrapper}
                  >
                    <img
                      src={url}
                      className={classes.image}
                      alt="attachment preview"
                    />
                  </button>
                  {deleteAttachmentBtn}
                </div>
              );
            }

            default: {
              return (
                <div
                  key={id}
                  className={classes.cardContainer}
                  ref={attachmentsContainerRef}
                >
                  <MediaCardWrapper
                    attachment={attachment}
                    deleteAttachment={deleteAttachment}
                    mediaCardSize={"medium"}
                    onClickAttachmentCard={onClickAttachmentCard}
                    mode={"view"}
                    isDeleteEnabled={false}
                    showRenameAttachmentOption={false}
                    showAttachmentAsPost={false}
                  />
                  {deleteAttachmentBtn}
                </div>
              );
            }
          }
        })}
      </div>
    );
  };

  // render code block as markdown view
  const renderCode = () => {
    if (_.isEmpty(code)) return null;

    const onClickCodeContainer = () => {
      setSelectedAction({ action: OPTIONS.CODE_EDITOR });
    };

    const onKeyDown = e => {
      onEnterOrSpaceKeyDown(e, onClickCodeContainer);
    };

    const deleteCode = e => {
      e.stopPropagation();

      updateAttachmentItems(prevItems => ({
        ...prevItems,
        CODE: "",
      }));
    };

    return (
      <div
        role="button"
        aria-label="code block"
        className={classes.codeContainer}
        onClick={onClickCodeContainer}
        onKeyDown={onKeyDown}
        tabIndex={0}
      >
        <MarkdownView
          content={code}
          contentContainerClass={classes.contentContainerClass}
        />

        {/* IconButton hasn't size smaller than 20px */}
        <button
          onClick={deleteCode}
          className={classes.deleteIcon}
          data-test-id={`${testId}-aiTutorChatBar-deleteCode`}
        >
          {<CloseCircleFilled size="xxx-small" variant="critical" />}
        </button>
      </div>
    );
  };

  // render graph as an image
  const renderGraph = () => {
    if (_.isEmpty(graph)) return null;

    const { metadata, thumbUrl } = graph || {};
    const { graphType, graphBase64Data } = metadata || {};

    const onGraphClick = () => {
      setSelectedItem({
        action: OPTIONS.ADD_GRAPH,
        params: {
          graphData: {
            ...graph,
            url: _.get(graph, "metadata.url"),
            type: graphType,
            graphBase64Data: graphBase64Data,
          },
        },
      });
    };

    const onKeyDown = e => {
      onEnterOrSpaceKeyDown(e, onGraphClick);
    };

    const deleteGraph = e => {
      e.stopPropagation();
      updateAttachmentItems(prevItems => ({
        ...prevItems,
        GRAPH: null,
      }));
    };

    const graphClass = classNames(
      classes.cardContainer,
      classes.imageContainer
    );

    return (
      <div
        role={"button"}
        className={graphClass}
        tabIndex={0}
        onClick={onGraphClick}
        onKeyDown={onKeyDown}
      >
        <img alt="graph-preview" src={thumbUrl} className={classes.graph} />
        <IconButton
          onClick={deleteGraph}
          _legacy_size="xxx-small"
          size="x-small"
          icon={<CloseOutlined />}
          className={classes.deleteIcon}
          testId={`${testId}-aiTutorChatBar-deleteGraph`}
        />
      </div>
    );
  };

  // render attachments, code, graph etc.
  const renderAttachmentItems = () => {
    return _.map(attachmentItems, (value, key) => {
      switch (key) {
        case "ATTACHMENTS": {
          const [audioAttachments, otherAttachments] = _.partition(
            attachments,
            { type: "AUDIO" }
          );

          return (
            <>
              {renderAttachments(otherAttachments)}
              {renderAttachments(audioAttachments)}
            </>
          );
        }
        case "CODE":
          return renderCode();
        case "GRAPH":
          return renderGraph();
        default:
          return null;
      }
    });
  };

  // render modals for math equation, code, graph etc.
  const renderModals = () => {
    const closeModal = () => setSelectedItem({ action: null });

    switch (selectedItem?.action) {
      case OPTIONS.ADD_IMAGE_FROM_CAMERA: {
        const updatePost = async ({ attachments }) => {
          if (!_.isEmpty(attachments)) {
            setSelectedAction({ action: null });

            const uploadedAttachments = await uploadAttachments({
              attachments,
              parentType: "AI_CONVERSATION",
              parentId: aiConversationId,
            });

            const attachmentArray = _.map(uploadedAttachments, attachment => {
              return _.omit(attachment, ATTACHMENT_OMIT_ATTRIBUTES);
            });

            addAttachment(attachmentArray);
          }
        };

        const screenPanelProps = {
          onCloseClick: closeModal,
          updatePost,
          postDetails: { attachments: [] },
        };

        return (
          <UIModal isOpen isFullScreen>
            <CaptureImage
              setToastMsg={setToastMsg}
              screenPanelProps={screenPanelProps}
              showDesignV2={true}
              hideGlobalTray={true}
              title={t("journal:take_photo")}
            />
          </UIModal>
        );
      }

      case OPTIONS.ADD_MATH_EQUATION: {
        const closeEditor = ({ latex = "" } = {}) => {
          // save only when user opened the editor to add new equation
          // if user is editing existing equation then do not save the latex

          if (_.isEmpty(selectedItem?.params?.equationId)) {
            updateAIData({ mathEquationLatex: latex });
          }

          closeModal();
        };

        const handleInsertEquation = params => {
          const { height, width, latex, verticalAlign, src } = params;

          // clear the math equation latex from redux once equation is inserted
          updateAIData({ mathEquationLatex: "" });

          const targetDataId = _.get(selectedItem, "params.equationId");

          // if user is creating new equation (targetDataId is undefined)
          // if caret position (savedSelectionRef) is saved then insert at that position

          if (!targetDataId && savedSelectionRef.current && !_.isEmpty(value)) {
            restoreCaretPositionAndInsertImage(params);
          } else {
            // if user is editing existing equation and update that equation
            const randomId = generateRandomIdV2();

            const imgElement = (
              <img
                src={src}
                className="math-equation"
                height={height}
                width={width}
                style={{
                  verticalAlign: `${verticalAlign}px`,
                  display: "inline",
                }}
                alt={latex}
                data-latex={latex}
                data-id={randomId}
              />
            );

            const newEquation = ReactDOMServer.renderToStaticMarkup(imgElement);

            const updatedValue = replaceMathEquation({
              value,
              newEquation,
              targetDataId,
            });

            updateInputField(updatedValue);
          }
        };

        return (
          <DSProvider dsVersion="1.0">
            <MathEquationEditor
              closeEditor={closeEditor}
              onInsertButtonClick={handleInsertEquation}
              latex={selectedItem?.params?.latex}
            />
          </DSProvider>
        );
      }

      case OPTIONS.CODE_EDITOR: {
        const onAddCodeHandler = code => {
          updateAttachmentItems(prevItems => ({
            ...prevItems,
            CODE: _.trim(code) || "",
          }));
        };

        return (
          <CodeEditor
            input={code}
            height={showCompactUI ? "30vh" : 670}
            width={910}
            onCloseHandler={closeModal}
            onAddCodeHandler={onAddCodeHandler}
          />
        );
      }

      case OPTIONS.ADD_GRAPH: {
        const handleGraphSave = (xmlUrl, thumbUrl, type) => {
          // Calculate graph base64 data
          const app = window.ggbApplet;
          const Base64GraphData = app.getBase64();

          // Handle graph edit
          if (!_.isEmpty(selectedItem?.params?.graphData)) {
            // Create updated attachment
            const { id } = selectedItem.params.graphData;

            const graphAttachment = {
              id,
              name: "Graph",
              thumbUrl,
              url: thumbUrl || null,
              metadata: {
                url: xmlUrl,
                isGraph: true,
                graphType: type,
                graphBase64Data: Base64GraphData,
              },
              mimeType: "image/jpeg",
              type: "IMAGE",
            };

            updateAttachmentItems(prevItems => ({
              ...prevItems,
              GRAPH: graphAttachment,
            }));

            return;
          }

          // If new graph is created, append it to attachments
          const graphAttachment = {
            id: generateRandomIdV2(),
            name: "Graph",
            thumbUrl,
            url: thumbUrl || null,
            metadata: {
              url: xmlUrl,
              isGraph: true,
              graphBase64Data: Base64GraphData,
              graphType: type,
            },
            mimeType: "image/jpeg",
            type: "IMAGE",
          };

          updateAttachmentItems(prevItems => ({
            ...prevItems,
            GRAPH: graphAttachment,
          }));
        };

        return (
          <Graph
            config={graphConfig}
            updateGraphData={handleGraphSave}
            showGraphImage={false}
            onClose={closeModal}
            value={_.get(selectedItem, "params.graphData")}
          />
        );
      }

      default:
        return null;
    }
  };

  const onSendMessageLocal = () => {
    onSendMessage({ audioAttachments });
  };

  const onKeyDownLocal = e => {
    onKeyDown({ e, audioAttachments });
  };

  const onClickEditMessageLocal = () => {
    onClickEditMessage({ audioAttachments });
  };

  // if every item is added (attachments, code, graph) then allow only 3 rows as the height exceeds the limit
  const maxRows =
    !_.isEmpty(attachmentItems) &&
    _.every(attachmentItems, item => !_.isEmpty(item))
      ? 3
      : 5;

  const availableChatOptions = getAvailableChatOptions(assessmentDetails);

  // check if any audio attachment has a blob URL (still processing)
  const hasAudioWithBlobURL =
    getIsAITutorEnhancementsV2Enabled() &&
    _.some(
      attachments,
      attachment =>
        attachment?.type === "AUDIO" && _.startsWith(attachment?.url, "blob:")
    );

  const enableAIPromptSetSelection = getSettingValue({
    name: "Common:EnableAIPromptSetSelection",
    readFromUserAndOrgLevel: true,
  });

  // classes

  const topInnerContainerClass = classNames({
    [classes.topInnerContainer]: true,
    [classes.hidden]: isRecording,
  });

  const innerContainerClass = classNames({
    [classes.innerContainer]: true,
    [classes.hidden]: isRecording,
  });

  const containerClass = classNames({
    [classes.container]: true,
    [classes.studentContainer]:
      userType === "student" || source === "STUDENT_PREVIEW",
  });

  const uiContentEditableStyle = showCompactUI
    ? styles.compactUIContentEditable
    : styles.uiContentEditable;

  return (
    <footer className={containerClass} ref={setAudioRecorderNodeRef}>
      {isSpeakingOnly && userAudioMessageCount < 2 ? (
        <div className={topInnerContainerClass}>
          <div className={classNames("text-body-s", classes.microphoneToSpeak)}>
            {t("ai:use_the_microphone_to_start_speaking")}
          </div>
        </div>
      ) : null}

      <div className={innerContainerClass}>
        {!isSpeakingOnly ? (
          <div className={classes.contentEditable}>
            {!isAudioTranscriptLoading ? (
              <UIBaseContentEditable
                value={value}
                placeholder={t(placeholder)}
                updateInputField={updateInputFieldLocal}
                style={uiContentEditableStyle}
                minRows={1}
                maxRows={maxRows}
                onKeyUp={saveCaretPosition}
                onKeyDown={onKeyDownLocal}
                updateInputRef={setContentEditableRef}
                onBlurInputField={onBlurInputField}
                onClickContentEditable={onClickContentEditable}
                onDoubleClick={onDoubleClickContentEditable}
                onPaste={onPaste}
                shouldParseInPlainText={false}
                disabled={isAudioTranscriptLoading}
              />
            ) : (
              <div className={classes.convertingToTextContainer}>
                <div className={classes.dotsAnimation} />
                <div className={classNames("text-body", classes.convertToText)}>
                  {t("ai:converting_to_text")}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* render attachments, code, graph  */}
        {renderAttachmentItems()}

        {/* action buttons (record audio, upload file etc.) */}
        <div className={classes.footerActions}>
          <div className={classes.footerLeftActions}>
            {_.map(availableChatOptions, option => {
              const { key, hasDivider = false } = option;
              const renderedOption = renderOption(option);
              return (
                <React.Fragment key={key}>
                  {renderedOption}
                  {hasDivider ? <div className={classes.divider} /> : null}
                </React.Fragment>
              );
            })}
          </div>
          <div className={classes.footerRightActions}>
            {enableAIPromptSetSelection ? (
              <>
                <ConfigureAIVoice
                  conversationId={aiConversationId}
                  gradeId={gradeId}
                  entityType={ASSESSMENT}
                  headerSubtitle={t("classRoom:ai_voice_modal_subtext")}
                  size={"small"}
                  promptSetEntityType={"ai_tutor_chat"}
                  customRef={null}
                  showLanguageDropdown={false}
                />

                <ConfigureAIVoice
                  conversationId={aiConversationId}
                  gradeId={gradeId}
                  entityType={ASSESSMENT}
                  headerSubtitle={t("classRoom:ai_voice_modal_subtext")}
                  size={"small"}
                  promptSetEntityType={"ai_tutor_evaluation"}
                  customRef={null}
                  showLanguageDropdown={false}
                />
              </>
            ) : null}

            {isEditMode ? (
              <div className={classes.footerButtonContainer}>
                <Button
                  size={iconSize}
                  dsVersion="2.0"
                  variant="neutral"
                  type="outlined"
                  onClick={() => {
                    setEditMessageId(null);
                    updateInputField("");
                    updateAttachmentItems({});
                  }}
                  testId={`${testId}-aiTutorChatBar-cancelEdit`}
                >
                  {t("common:cancel")}
                </Button>
                <Button
                  size={iconSize}
                  dsVersion="2.0"
                  variant="progressive"
                  onClick={onClickEditMessageLocal}
                  testId={`${testId}-aiTutorChatBar-editMessage`}
                >
                  {t("common:edit")}
                </Button>
              </div>
            ) : hasAudioWithBlobURL ? (
              <div className={classes.dotsAnimationContainer}>
                <LoaderSVG size={"x-small"} />
              </div>
            ) : (
              <IconButton
                size={iconSize}
                dsVersion={"2.0"}
                variant={"progressive"}
                icon={
                  <SendFilled
                    variant={"on"}
                    disabled={isSendDisabled || isAudioTranscriptLoading}
                  />
                }
                onClick={onSendMessageLocal}
                disabled={isSendDisabled || isAudioTranscriptLoading}
                testId={`${testId}-aiTutorChatBar-sendMessage`}
              />
            )}
          </div>
        </div>

        {/* Left arrow icon to scroll to left attachments */}
        {showLeftIcon ? (
          <IconButton
            onClick={handleScrollLeft}
            icon={<ChevronLeftOutlined />}
            size={"x-small"}
            dsVersion={"2.0"}
            variant={"neutral"}
            type={"outlined"}
            className={classes.leftArrow}
            testId={`${testId}-aiTutorChatBar-scrollLeft`}
          />
        ) : null}

        {/* Right arrow icon to scroll to right attachments */}

        {showRightIcon ? (
          <IconButton
            onClick={handleScrollRight}
            icon={<ChevronRightOutlined />}
            size={"x-small"}
            dsVersion={"2.0"}
            variant={"neutral"}
            type={"outlined"}
            className={classes.rightArrow}
            testId={`${testId}-aiTutorChatBar-scrollRight`}
          />
        ) : null}
      </div>

      {/* render math equation editor, graph, code etc. */}
      {renderModals()}

      {/* preview attachments */}
      {showMediaModal ? (
        <MediaModal
          mode={"view"}
          toggleMediaModal={() => setShowMediaModal(prevState => !prevState)}
          attachments={attachments}
          activeIndex={selectedAttachmentIndex}
          showWorkBook={false}
          showPdfTronEditButton={false}
          showPdfTronfeedback={false}
        />
      ) : null}
    </footer>
  );
};

const mapStateToProps = state => {
  const userType = getUserType(state);

  const progressOfUploads = _.get(state, "app_services.progressOfUploads");
  const mathEquationLatex = _.get(state, "classRoom.aiData.mathEquationLatex");

  const isUploading = _.some(_.keys(progressOfUploads), key => {
    const resource = progressOfUploads[key];
    return resource?.parentType == "AI_CONVERSATION";
  });

  return {
    userType,
    isUploading,
    mathEquationLatex,
  };
};

const mapActionCreators = {
  setToastMsg,
  uploadAttachments,
  updateAIData,
  getSpeechToTextTranscript,
  hideIntercom,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  AITutorChatBar
);
